import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { ToggleType, useCollapseItems, useOpenNextRule, useSelectedItemState } from '@wix/rules-panel-ui';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { isApplyTriggeredAtom, panelStateAtom, verticalsDrillInPageIdAtom } from '../atoms/session-atoms';
import { BiCtaNames, BiModalTypes } from '../bi/types';
import { showFailedFetchRuleExecutionResultsModal } from '../components/modals/show-failed-fetch-rule-execution-results';
import { SITE_LEVEL_PAGE_ID } from '../consts';
import { useQueryRuleExecutionResultsQuery } from '../queries/use-query-rule-execution-results-query';
import { useBuildMembersCustomPageRuleGroups } from '../rule-mappers/build-rule-props/use-build-members-custom-page-rule-group';
import { useBuildPageLevelRuleGroups } from '../rule-mappers/build-rule-props/use-build-page-level-rule-group';
import { useBuildSiteLevelRuleGroupV2 } from '../rule-mappers/build-rule-props/use-build-site-level-rule-group-v2';
import { useBuildVerticalRuleGroups } from '../rule-mappers/build-rule-props/use-build-vertical-group';
import { getPageTypeForBi } from '../rule-mappers/shared/bi-helpers';
import { AnalysisState, usePanelStateAtom } from './rules-state';
import { useA11yWizardAdapter } from './use-a11y-wizard-adapter';
import { useBiLogger } from './use-bi-logger';
import { useNavigation } from './use-navigation';
export const useRuleGroupsBuilderV2 = () => {
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    experiments
  } = useExperiments();
  const isInaccessibleComponentsRuleEnabled = experiments.enabled('specs.responsive-editor.EnableInaccessibleComponentsRule');
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    buildSiteLevelRuleGroupV2
  } = useBuildSiteLevelRuleGroupV2();
  const {
    buildPageLevelRuleGroup
  } = useBuildPageLevelRuleGroups();
  const {
    buildMembersCustomPageRuleGroup
  } = useBuildMembersCustomPageRuleGroups();
  const [isApplyTriggered, setIsApplyTriggered] = useAtom(isApplyTriggeredAtom);
  const {
    collapseGroup
  } = useCollapseItems();
  const [pageIdToFetchRuleResults, setPageIdToFetchRuleResults] = useState(undefined);
  const {
    setGroupData,
    getPagesData
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const {
    selectedRule,
    setSelectedItem,
    selectedGroup
  } = useSelectedItemState();
  const {
    openNextRule
  } = useOpenNextRule({
    selectedGroup,
    selectedRule,
    setSelectedItem
  });
  const setVerticalsDrillInPageIdAtom = useSetAtom(verticalsDrillInPageIdAtom);
  const navigateToPage = useNavigation();
  const showFetchErrorModal = refetch => showFailedFetchRuleExecutionResultsModal({
    a11yWizardAdapter,
    biLogger,
    editorHost,
    errorMessage: t('accessibility-wizard.fetch-rule-execution-results.failed-to-fetch-results-modal.content'),
    handleCancel: () => {
      biLogger.modalCtaClick({
        ctaName: BiCtaNames.Cancel,
        modalType: BiModalTypes.UnableToFetchRuleExecutionResults
      });
      setGroupData(pageIdToFetchRuleResults ?? '', {
        analysisState: AnalysisState.AnalyzeDonePendingResults
      });
      setPageIdToFetchRuleResults(undefined);
      collapseGroup();
    },
    handleTryAgain: refetch,
    t,
    title: t('accessibility-wizard.fetch-rule-execution-results.failed-to-fetch-results-modal.title')
  });
  const {
    buildVerticalLevelRuleGroup
  } = useBuildVerticalRuleGroups();
  const {
    refetch: refetchResultsByPage
  } = useQueryRuleExecutionResultsQuery(pageIdToFetchRuleResults, () => showFetchErrorModal(refetchResultsByPage));
  const {
    groupsDataMap
  } = useAtomValue(panelStateAtom);
  const ruleGroupsV2 = useMemo(() => {
    if (groupsDataMap.size === 0) {
      return [];
    }
    const pageLevelGroups = [];
    let siteLevelGroup = {};
    pagesData.entries().forEach((_ref, index) => {
      let [pageId, pageData] = _ref;
      if (pageId === SITE_LEVEL_PAGE_ID) {
        siteLevelGroup = buildSiteLevelRuleGroupV2({
          onOpen: () => {
            setPageIdToFetchRuleResults(SITE_LEVEL_PAGE_ID);
          }
        });
      } else if (pageData.tpaPage) {
        pageLevelGroups.push(buildVerticalLevelRuleGroup({
          onClick: () => {
            var _pagesData$get;
            setPageIdToFetchRuleResults(pageId);
            setVerticalsDrillInPageIdAtom(pageId);
            navigateToPage(pageId);
            biLogger.pageExpandCollapse({
              initiatedByUser: true,
              pageId,
              pageIndex: index,
              pageType: getPageTypeForBi(((_pagesData$get = pagesData.get(pageId)) == null ? void 0 : _pagesData$get.itemType) ?? '', true),
              ruleResults: [],
              toggleType: ToggleType.Expand
            });
          },
          pageId
        }));
      } else if (pageData.isMembershipCustomPage) {
        if (isInaccessibleComponentsRuleEnabled) {
          pageLevelGroups.push(buildMembersCustomPageRuleGroup({
            onOpen: () => {
              setPageIdToFetchRuleResults(pageId);
            },
            pageId
          }));
        }
      } else {
        pageLevelGroups.push(buildPageLevelRuleGroup({
          onOpen: () => {
            setPageIdToFetchRuleResults(pageId);
          },
          pageId
        }));
      }
    });
    const homePageId = a11yWizardAdapter.getHomePageId();
    const homePageGroup = pageLevelGroups.filter(group => group.groupId.includes(homePageId));
    const otherPageGroups = pageLevelGroups.filter(group => !group.groupId.includes(homePageId));
    const sortedRuleGroups = [siteLevelGroup, ...homePageGroup, ...otherPageGroups];
    if (isApplyTriggered) {
      setIsApplyTriggered(false);
      openNextRule(sortedRuleGroups);
    }
    return sortedRuleGroups;
  }, [groupsDataMap.size, pagesData, a11yWizardAdapter, isApplyTriggered, isInaccessibleComponentsRuleEnabled, buildSiteLevelRuleGroupV2, buildVerticalLevelRuleGroup, setVerticalsDrillInPageIdAtom, navigateToPage, biLogger, buildMembersCustomPageRuleGroup, buildPageLevelRuleGroup, setIsApplyTriggered, openNextRule]);
  return ruleGroupsV2;
};
//# sourceMappingURL=use-rule-groups-builder-v2.js.map